import React, { useCallback, useEffect, useState } from "react";
import { Table, Form, Button, Spinner, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Common } from "../../../../utils";
import {
  addSuperAdminTicketComment,
  getSuperAdminTicket,
  getSuperAdminTickets,
  updateSuperAdminTicket,
} from "./superAdminActions";
import {
  PRIORITY_OPTIONS,
  PriorityBadge,
  STATUS_OPTIONS,
  StatusBadge,
} from "../../helpSupport/helpSupportShared";
import "../../helpSupport/HelpSupport.css";

const formatSaDate = (value, { withTime = false } = {}) => {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    const options = withTime
      ? {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      : { month: "short", day: "numeric", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  } catch {
    return "—";
  }
};

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const normalizeTicketRow = (t) => ({
  id: pick(t, "id", "Id"),
  title: pick(t, "title", "Title"),
  problemType: pick(t, "problemType", "ProblemType"),
  status: pick(t, "status", "Status"),
  statusName: pick(t, "statusName", "StatusName"),
  priority: pick(t, "priority", "Priority"),
  priorityName: pick(t, "priorityName", "PriorityName"),
  companyId: pick(t, "companyId", "CompanyId"),
  companyName: pick(t, "companyName", "CompanyName"),
  createdBy: pick(t, "createdBy", "CreatedBy"),
  createdByName: pick(t, "createdByName", "CreatedByName"),
  createdByEmail: pick(t, "createdByEmail", "CreatedByEmail"),
  createdByIsCompanyAdmin: Boolean(pick(t, "createdByIsCompanyAdmin", "CreatedByIsCompanyAdmin")),
  createdByType:
    pick(t, "createdByType", "CreatedByType") ||
    (Boolean(pick(t, "createdByIsCompanyAdmin", "CreatedByIsCompanyAdmin"))
      ? "Company Admin"
      : "Company User"),
  assignedToName: pick(t, "assignedToName", "AssignedToName"),
  createdDate: pick(t, "createdDate", "CreatedDate"),
  updatedDate: pick(t, "updatedDate", "UpdatedDate"),
  resolvedDate: pick(t, "resolvedDate", "ResolvedDate"),
  commentCount: pick(t, "commentCount", "CommentCount") ?? 0,
});

const normalizeTicketDetail = (t) => ({
  ...normalizeTicketRow(t),
  description: pick(t, "description", "Description") || "",
  comments: (pick(t, "comments", "Comments") || []).map((c) => ({
    id: pick(c, "id", "Id"),
    comment: pick(c, "comment", "Comment") || "",
    commentedByName: pick(c, "commentedByName", "CommentedByName") || "—",
    commentedDate: pick(c, "commentedDate", "CommentedDate"),
    isInternal: Boolean(pick(c, "isInternal", "IsInternal")),
  })),
});

const SuperAdminTicketsPanel = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [adminForm, setAdminForm] = useState({ status: "", priority: "" });

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSuperAdminTickets({
        search,
        status: statusFilter === "" ? undefined : Number(statusFilter),
        page,
        pageSize: 50,
      });
      if (res?.success) {
        setTickets((res.data || []).map(normalizeTicketRow));
        setTotal(res.total || 0);
      } else {
        Common.showErrorMessage(res?.message || "Failed to load tickets");
      }
    } catch (err) {
      console.error(err);
      Common.showErrorMessage("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const res = await getSuperAdminTicket(id);
      if (res?.success) {
        const ticket = normalizeTicketDetail(res.data);
        setDetail(ticket);
        setAdminForm({
          status: String(ticket.status ?? ""),
          priority: String(ticket.priority ?? ""),
        });
      } else {
        Common.showErrorMessage(res?.message || "Failed to load ticket");
        setDetail(null);
      }
    } catch (err) {
      console.error(err);
      Common.showErrorMessage("Failed to load ticket");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const openTicket = (id) => {
    setSelectedId(id);
    setComment("");
    setIsInternal(false);
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!selectedId || !comment.trim()) {
      Common.showErrorMessage(
        t("app.helpSupport.commentRequired", { defaultValue: "Please enter a comment." })
      );
      return;
    }
    setSaving(true);
    try {
      const res = await addSuperAdminTicketComment({
        ticketId: selectedId,
        comment: comment.trim(),
        isInternal,
      });
      if (!res?.success) {
        Common.showErrorMessage(res?.message || "Failed to add comment");
        return;
      }
      setComment("");
      setIsInternal(false);
      Common.showSuccessMessage(
        t("app.helpSupport.commentAdded", { defaultValue: "Comment added." })
      );
      await Promise.all([loadDetail(selectedId), loadTickets()]);
    } catch (err) {
      console.error(err);
      Common.showErrorMessage(err?.message || "Failed to add comment");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTicket = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const res = await updateSuperAdminTicket({
        ticketId: selectedId,
        status: adminForm.status === "" ? null : Number(adminForm.status),
        priority: adminForm.priority === "" ? null : Number(adminForm.priority),
      });
      if (!res?.success) {
        Common.showErrorMessage(res?.message || "Failed to update ticket");
        return;
      }
      setDetail(normalizeTicketDetail(res.data));
      Common.showSuccessMessage(
        t("app.helpSupport.updatedSuccess", { defaultValue: "Ticket updated." })
      );
      await loadTickets();
    } catch (err) {
      console.error(err);
      Common.showErrorMessage(err?.message || "Failed to update ticket");
    } finally {
      setSaving(false);
    }
  };

  if (selectedId) {
    return (
      <div className="help-support-page sa-ticket-detail">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button variant="outline-secondary" size="sm" onClick={closeDetail}>
            <i className="bi bi-arrow-left me-1" />
            {t("app.common.back", { defaultValue: "Back to tickets" })}
          </Button>
        </div>

        {detailLoading || !detail ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <>
            <div className="help-support-detail-card">
              <div className="help-support-badges" style={{ marginBottom: "0.75rem" }}>
                <StatusBadge status={detail.status} statusName={detail.statusName} />
                <PriorityBadge priority={detail.priority} priorityName={detail.priorityName} />
              </div>
              <h2 className="help-support-detail-title">{detail.title}</h2>
              <div className="help-support-ticket-meta">
                <span>{detail.companyName || detail.companyId || "—"}</span>
                <span>{detail.problemType || "—"}</span>
                <span>
                  {t("app.helpSupport.createdBy", { defaultValue: "Created by" })}:{" "}
                  {detail.createdByName || "—"}
                  {detail.createdByEmail ? ` (${detail.createdByEmail})` : ""}
                </span>
                <span>
                  <Badge bg={detail.createdByIsCompanyAdmin ? "primary" : "secondary"}>
                    {detail.createdByType ||
                      (detail.createdByIsCompanyAdmin ? "Company Admin" : "Company User")}
                  </Badge>
                </span>
                {detail.assignedToName ? (
                  <span>
                    {t("app.helpSupport.assignedTo", { defaultValue: "Assigned to" })}:{" "}
                    {detail.assignedToName}
                  </span>
                ) : null}
                <span>
                  {t("app.helpSupport.created", { defaultValue: "Created" })}:{" "}
                  {formatSaDate(detail.createdDate, { withTime: true })}
                </span>
                <span>
                  {t("app.helpSupport.updated", { defaultValue: "Updated" })}:{" "}
                  {formatSaDate(detail.updatedDate, { withTime: true })}
                </span>
              </div>
              <p className="help-support-detail-description">{detail.description}</p>

              <div className="help-support-admin-panel">
                <div className="help-support-field">
                  <label htmlFor="sa-admin-status">
                    {t("app.helpSupport.status", { defaultValue: "Status" })}
                  </label>
                  <select
                    id="sa-admin-status"
                    value={adminForm.status}
                    onChange={(e) => setAdminForm((prev) => ({ ...prev, status: e.target.value }))}
                    disabled={saving}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="help-support-field">
                  <label htmlFor="sa-admin-priority">
                    {t("app.helpSupport.priority", { defaultValue: "Priority" })}
                  </label>
                  <select
                    id="sa-admin-priority"
                    value={adminForm.priority}
                    onChange={(e) =>
                      setAdminForm((prev) => ({ ...prev, priority: e.target.value }))
                    }
                    disabled={saving}
                  >
                    {PRIORITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="help-support-field" style={{ justifyContent: "flex-end" }}>
                  <label>&nbsp;</label>
                  <button
                    type="button"
                    className="help-support-primary-btn"
                    onClick={handleUpdateTicket}
                    disabled={saving}
                  >
                    {t("app.helpSupport.saveChanges", { defaultValue: "Save changes" })}
                  </button>
                </div>
              </div>
            </div>

            <div className="help-support-detail-card">
              <h3 className="help-support-section-title">
                {t("app.helpSupport.activity", { defaultValue: "Activity" })}
              </h3>

              {detail.comments.length === 0 ? (
                <div className="help-support-empty">
                  {t("app.helpSupport.noComments", {
                    defaultValue: "No updates yet. Add a comment to continue the conversation.",
                  })}
                </div>
              ) : (
                <div className="help-support-comments">
                  {detail.comments.map((c) => (
                    <div
                      key={c.id}
                      className={`help-support-comment ${c.isInternal ? "is-internal" : ""}`}
                    >
                      <div className="help-support-comment-head">
                        <span className="help-support-comment-author">{c.commentedByName}</span>
                        <span>{formatSaDate(c.commentedDate, { withTime: true })}</span>
                        {c.isInternal ? (
                          <span className="help-support-badge help-support-badge--internal">
                            {t("app.helpSupport.internal", { defaultValue: "Internal" })}
                          </span>
                        ) : null}
                      </div>
                      <p className="help-support-comment-body">{c.comment}</p>
                    </div>
                  ))}
                </div>
              )}

              <form className="help-support-comment-box" onSubmit={handleAddComment}>
                <label htmlFor="sa-ticket-comment">
                  {t("app.helpSupport.addComment", { defaultValue: "Add a reply" })}
                </label>
                <textarea
                  id="sa-ticket-comment"
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={saving}
                  placeholder={t("app.helpSupport.commentPlaceholder", {
                    defaultValue: "Write a reply for the requester…",
                  })}
                />
                <div className="help-support-comment-actions">
                  <label className="help-support-checkbox">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      disabled={saving}
                    />
                    {t("app.helpSupport.internalNote", {
                      defaultValue: "Internal note (hidden from requester)",
                    })}
                  </label>
                  <button type="submit" className="help-support-primary-btn" disabled={saving}>
                    {t("app.helpSupport.postComment", { defaultValue: "Post reply" })}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <Form
        className="d-flex flex-wrap gap-2 mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          loadTickets();
        }}
      >
        <Form.Control
          type="search"
          placeholder="Search title, company, or requester…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 220, flex: 1 }}
        />
        <Form.Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: 180 }}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
        <Button type="submit" variant="primary">
          Search
        </Button>
      </Form>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <>
          <div className="table-responsive sa-table-wrap">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Company</th>
                  <th>Requester</th>
                  <th>Raised by</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-muted text-center py-4">
                      No tickets found
                    </td>
                  </tr>
                ) : (
                  tickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td>
                        <div className="fw-semibold">{ticket.title}</div>
                        <div className="text-muted small">
                          {ticket.problemType || "—"}
                          {ticket.commentCount > 0 ? ` · ${ticket.commentCount} replies` : ""}
                        </div>
                      </td>
                      <td>{ticket.companyName || "—"}</td>
                      <td>
                        <div>{ticket.createdByName || "—"}</div>
                        <div className="text-muted small">{ticket.createdByEmail || ""}</div>
                      </td>
                      <td>
                        <Badge bg={ticket.createdByIsCompanyAdmin ? "primary" : "secondary"}>
                          {ticket.createdByType ||
                            (ticket.createdByIsCompanyAdmin ? "Company Admin" : "Company User")}
                        </Badge>
                      </td>
                      <td>
                        <StatusBadge status={ticket.status} statusName={ticket.statusName} />
                      </td>
                      <td>
                        <PriorityBadge
                          priority={ticket.priority}
                          priorityName={ticket.priorityName}
                        />
                      </td>
                      <td>{formatSaDate(ticket.updatedDate)}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openTicket(ticket.id)}
                        >
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">{total} tickets</span>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page * 50 >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SuperAdminTicketsPanel;
